import { useState } from 'react';
import { Search, Clock, ArrowRight, ExternalLink, Loader2, ChevronRight } from 'lucide-react';
import { useTransactions } from '../hooks/useExchange';
import { useAuth } from '../context/AuthContext';
import CurrencyIcon from '../components/CurrencyIcon';
import './ExchangeHistory.css';

const ExchangeHistory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { isAuthenticated } = useAuth();
  const { data, isLoading, error, refetch } = useTransactions();

  const transactions = data?.transactions || [];

  const filteredTransactions = transactions.filter(tx => 
    tx.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tx.fromCurrency?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tx.toCurrency?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      waiting: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.3)' },
      confirming: { bg: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', border: 'rgba(59, 130, 246, 0.3)' },
      exchanging: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' },
      sending: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' },
      completed: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', border: 'rgba(16, 185, 129, 0.3)' },
      finished: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', border: 'rgba(16, 185, 129, 0.3)' },
      failed: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' },
      refunded: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.3)' },
    };
    
    const style = styles[status] || styles.waiting;
    
    return (
      <span 
        className="status-badge"
        style={{ 
          background: style.bg, 
          color: style.color,
          border: `1px solid ${style.border}`
        }}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Format transaction ID for display
  const formatTxId = (id) => {
    if (!id) return '';
    if (id.length <= 20) return id;
    return `${id.slice(0, 12)}...${id.slice(-8)}`;
  };

  if (isLoading) {
    return (
      <div className="history-page">
        <div className="page-container">
          <div className="page-header">
            <h1>Exchange <span className="gradient-text">History</span></h1>
            <p>View and track your past cryptocurrency exchanges</p>
          </div>
          <div className="history-card loading">
            <Loader2 className="spinner" />
            <p>Loading your exchange history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-page">
        <div className="page-container">
          <div className="page-header">
            <h1>Exchange <span className="gradient-text">History</span></h1>
            <p>View and track your past cryptocurrency exchanges</p>
          </div>
          <div className="history-card error">
            <p>Failed to load exchange history</p>
            <button onClick={() => refetch()} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="page-container">
        <div className="page-header">
          <h1>Exchange <span className="gradient-text">History</span></h1>
          <p>View and track your past cryptocurrency exchanges</p>
          {!isAuthenticated && (
            <p className="guest-notice">
              <Clock size={14} />
              Guest transactions are stored temporarily. 
              <a href="/login">Sign in</a> to save them permanently.
            </p>
          )}
        </div>

        <div className="history-card">
          <div className="history-toolbar">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search by ID or currency..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {transactions.length > 0 && (
              <span className="tx-count">{transactions.length} transaction{transactions.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {filteredTransactions.length === 0 ? (
            <div className="empty-history">
              <Clock className="empty-icon" />
              <h3>No exchanges found</h3>
              <p>{searchQuery ? 'Try a different search term' : 'Your exchange history will appear here'}</p>
              <a href="/" className="start-btn">Start Exchange</a>
            </div>
          ) : (
            <div className="history-list">
              {filteredTransactions.map((tx) => (
                <a key={tx.id} href={`/tx/${tx.id}`} className="history-item">
                  {/* Desktop Layout */}
                  <div className="history-item-desktop">
                    <div className="tx-info">
                      <div className="tx-pair">
                        <CurrencyIcon 
                          currency={{ 
                            id: tx.fromCurrencyId || tx.fromCurrency?.toLowerCase(), 
                            ticker: tx.fromCurrency?.toLowerCase(),
                            name: tx.fromCurrency
                          }}
                          size="md"
                          className="currency-img"
                        />
                        <ArrowRight className="arrow" />
                        <CurrencyIcon 
                          currency={{ 
                            id: tx.toCurrencyId || tx.toCurrency?.toLowerCase(), 
                            ticker: tx.toCurrency?.toLowerCase(),
                            name: tx.toCurrency
                          }}
                          size="md"
                          className="currency-img"
                        />
                      </div>
                      <div className="tx-details">
                        <span className="tx-amount">
                          {tx.fromAmount} {tx.fromCurrency?.toUpperCase()} → {tx.toAmount} {tx.toCurrency?.toUpperCase()}
                        </span>
                        <span className="tx-id" title={tx.id}>ID: {formatTxId(tx.id)}</span>
                      </div>
                    </div>
                    
                    <div className="tx-meta">
                      {getStatusBadge(tx.status)}
                      <span className="tx-date">
                        {new Date(tx.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <span className="view-btn">
                      View
                      <ExternalLink />
                    </span>
                  </div>

                  {/* Mobile Layout - Card Style */}
                  <div className="history-item-mobile">
                    {/* Top Row: Icons + Status */}
                    <div className="mobile-header">
                      <div className="mobile-pair">
                        <div className="mobile-icon">
                          <CurrencyIcon 
                            currency={{ 
                              id: tx.fromCurrencyId || tx.fromCurrency?.toLowerCase(), 
                              ticker: tx.fromCurrency?.toLowerCase(),
                              name: tx.fromCurrency
                            }}
                            size="lg"
                          />
                          <span className="mobile-currency-code">{tx.fromCurrency?.toUpperCase()}</span>
                        </div>
                        <ArrowRight className="mobile-arrow" />
                        <div className="mobile-icon">
                          <CurrencyIcon 
                            currency={{ 
                              id: tx.toCurrencyId || tx.toCurrency?.toLowerCase(), 
                              ticker: tx.toCurrency?.toLowerCase(),
                              name: tx.toCurrency
                            }}
                            size="lg"
                          />
                          <span className="mobile-currency-code">{tx.toCurrency?.toUpperCase()}</span>
                        </div>
                      </div>
                      {getStatusBadge(tx.status)}
                    </div>

                    {/* Middle Row: Amounts */}
                    <div className="mobile-amounts">
                      <div className="mobile-amount-item">
                        <span className="mobile-label">You Sent</span>
                        <span className="mobile-value">{tx.fromAmount} {tx.fromCurrency?.toUpperCase()}</span>
                      </div>
                      <div className="mobile-amount-item">
                        <span className="mobile-label">You Received</span>
                        <span className="mobile-value">{tx.toAmount} {tx.toCurrency?.toUpperCase()}</span>
                      </div>
                    </div>

                    {/* Bottom Row: ID and Date */}
                    <div className="mobile-footer">
                      <div className="mobile-tx-info">
                        <span className="mobile-id" title={tx.id}>{formatTxId(tx.id)}</span>
                        <span className="mobile-date">{new Date(tx.createdAt).toLocaleDateString()}</span>
                      </div>
                      <ChevronRight className="mobile-chevron" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExchangeHistory;
